import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import Modal from './Modal';

// Dialog di conferma custom (sostituisce window.confirm del browser, che stona
// con l'interfaccia e non è personalizzabile). Uso:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title, message, confirmLabel, tone: 'danger' }))) return;
//
// Un unico host renderizzato a livello di app: qualunque componente sotto
// <ConfirmProvider> può chiamare useConfirm(). La promise si risolve con true
// (conferma) o false (annulla / Esc / click fuori / X).
const ConfirmContext = createContext(null);

const DEFAULTS = {
  title: 'Conferma',
  message: '',
  confirmLabel: 'Conferma',
  cancelLabel: 'Annulla',
  tone: 'primary', // primary | warn | danger → classe btn-*
  icon: 'alert',
  // Con hideCancel resta il solo pulsante di chiusura: serve ai dialoghi che
  // informano e basta (es. "azione non consentita"), dove "Annulla" non avrebbe
  // senso perché non c'è nulla da annullare.
  hideCancel: false,
};

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((options = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setOpts({ ...DEFAULTS, ...options });
  }), []);

  const settle = useCallback((result) => {
    if (resolver.current) { resolver.current(result); resolver.current = null; }
    setOpts(null);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {opts && (
        <Modal title={opts.title} icon={opts.icon} onClose={() => settle(false)}>
          <div className="modal-body">
            <p style={{ whiteSpace: 'pre-line', margin: 0 }}>{opts.message}</p>
            <div className="modal-actions">
              {!opts.hideCancel && (
                <button type="button" className="btn btn-ghost" onClick={() => settle(false)}>{opts.cancelLabel}</button>
              )}
              <button type="button" className={`btn btn-${opts.tone}`} onClick={() => settle(true)}>{opts.confirmLabel}</button>
            </div>
          </div>
        </Modal>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm deve essere usato dentro <ConfirmProvider>');
  return ctx;
}

export default ConfirmProvider;
